import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { OptionalBenefitComputationFn } from '../EstimateDetailsAndOptions';
import { SelectedEstimateOption } from '../../../types/estimates/SelectedEstimateOptionType';
import { useMemo } from 'react';
import { getBenefitTypeName } from '@/utils/benefits/getBenefitTypeName';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { addDays } from 'date-fns';
import { useGetFileUrl } from '@/hooks/useFile';
import EstimateDayStepCarRentalSummaryCard from './EstimateDayStepCarRentalSummaryCard';
import EstimateDayStepHotelSummaryCard from './EstimateDayStepHotelSummaryCard';
import EstimateDayStepCardTemplate from './EstimateDayStepCardTemplate';
import { SupportedLocale } from '@/types/supported-locale.type';

interface EstimateDayStepSummaryCardProps {
  step: CircuitDayFormData['steps'][number]['transport'];
  date: Date;
  selectedOptions: Record<string, SelectedEstimateOption>;
  dayIndex: number;
  computeOptionalBenefitPrice: OptionalBenefitComputationFn;
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  language?: SupportedLocale;
}

const EstimateDayStepSummaryCard = ({
  step,
  date,
  selectedOptions,
  dayIndex,
  computeOptionalBenefitPrice,
  currency = 'EUR',
  creationDate,
  accessToken,
  language,
}: EstimateDayStepSummaryCardProps) => {
  const standardBenefit = step.standardBenefit;
  const selectedBenefit = useMemo(
    () =>
      selectedOptions[step.id]
        ? selectedOptions[step.id].benefitId === standardBenefit?.id
          ? standardBenefit
          : step.optionalBenefits.find(
              ({ id }) => selectedOptions[step.id].benefitId === id,
            )
        : standardBenefit,
    [selectedOptions, standardBenefit, step.id, step.optionalBenefits],
  );

  const stepWithOnlySelectedBenefit = useMemo(
    () => ({ ...step, standardBenefit: selectedBenefit, optionalBenefits: [] }),
    [selectedBenefit, step],
  );

  const type: BenefitType | null = useMemo(
    () =>
      selectedBenefit
        ? getBenefitTypeName(
            selectedBenefit.type === 'ACTIVITY'
              ? (selectedBenefit.activity?.activityType ?? selectedBenefit.type)
              : selectedBenefit.type,
          )
        : null,
    [selectedBenefit],
  );

  const stepDates = useMemo(() => {
    if (!step.nbDays || step.nbDays <= 1) return null;
    return {
      startDate: date,
      endDate: addDays(date, step.nbDays - 1),
    };
  }, [date, step.nbDays]);

  const images = useMemo(() => {
    return (
      selectedBenefit?.activity?.images ??
      selectedBenefit?.VehicleWithChauffeur?.images ??
      []
    );
  }, [
    selectedBenefit?.VehicleWithChauffeur?.images,
    selectedBenefit?.activity?.images,
  ]);

  const mainImageKey = useMemo(() => {
    return images.length > 0 ? images[0] : null;
  }, [images]);

  const { data: imageUrl } = useGetFileUrl(mainImageKey, false, accessToken);

  const standardName = useMemo(() => {
    return language
      ? (selectedBenefit?.translations?.[language]?.name ??
          selectedBenefit?.name ??
          '')
      : (selectedBenefit?.name ?? '');
  }, [language, selectedBenefit?.name, selectedBenefit?.translations]);

  if (!type) return null;

  if (type === BenefitType.VEHICLE)
    return (
      <EstimateDayStepCarRentalSummaryCard
        step={stepWithOnlySelectedBenefit}
        computeOptionalBenefitPrice={computeOptionalBenefitPrice}
        selectedOptions={selectedOptions}
        date={date}
        dayIndex={dayIndex}
        language={language}
        currency={currency}
        creationDate={creationDate}
      />
    );

  if (type === BenefitType.HOTEL)
    return (
      <EstimateDayStepHotelSummaryCard
        step={stepWithOnlySelectedBenefit}
        selectedOptions={selectedOptions}
        currency={currency}
        stepDates={stepDates}
        creationDate={creationDate}
        language={language}
      />
    );

  return (
    <EstimateDayStepCardTemplate
      step={stepWithOnlySelectedBenefit}
      type={type}
      stepDates={stepDates}
      standardName={standardName}
      language={language}
      titlePrice={selectedOptions[step.id]?.deltaPrice ?? undefined}
      displayOptionalBenefits={false}
      setDisplayOptionalBenefits={() => {}}
      imageUrl={imageUrl?.url}
      imagesKeys={images}
      shouldDisplayDetailsBtn={false}
      shouldDisplayReadMoreBtn={false}
      currency={currency}
      creationDate={creationDate}
      accessToken={accessToken}
    />
  );
};

export default EstimateDayStepSummaryCard;
